import { z } from 'zod';
import { uuidSchema, isoDateSchema, timestampedSchema } from '../schemas/common';
import { timeOfDaySchema } from '../schemas/class';

export const attendanceStatusSchema = z.enum(['present', 'late', 'absent', 'excused']);
export type AttendanceStatus = z.infer<typeof attendanceStatusSchema>;

export const classSessionSchema = timestampedSchema.extend({
  class_id: uuidSchema,
  session_date: isoDateSchema,
  start_time: timeOfDaySchema,
  end_time: timeOfDaySchema,
  room: z.string().min(1),
  cancelled: z.boolean(),
  cancellation_reason: z.string().nullable(),
  notes: z.string().nullable(),
});
export type ClassSession = z.infer<typeof classSessionSchema>;

export const attendanceRecordInputSchema = z.object({
  class_session_id: uuidSchema,
  dancer_id: uuidSchema,
  status: attendanceStatusSchema,
  notes: z.string().max(500).optional(),
});
export type AttendanceRecordInput = z.infer<typeof attendanceRecordInputSchema>;

export const attendanceRecordSchema = timestampedSchema.merge(attendanceRecordInputSchema).extend({
  marked_by: uuidSchema.nullable(),
});
export type AttendanceRecord = z.infer<typeof attendanceRecordSchema>;

// Per-dancer attendance summary across a span of sessions — computed in the
// UI from a list of attendance records.
export interface AttendanceSummary {
  present: number;
  late: number;
  absent: number;
  excused: number;
  total: number;
  rate: number; // present + late, as fraction of total
}

export function summarizeAttendance(records: AttendanceRecord[]): AttendanceSummary {
  const sum: AttendanceSummary = {
    present: 0,
    late: 0,
    absent: 0,
    excused: 0,
    total: records.length,
    rate: 0,
  };
  for (const r of records) {
    sum[r.status] += 1;
  }
  if (sum.total > 0) {
    sum.rate = (sum.present + sum.late) / sum.total;
  }
  return sum;
}
